import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// === ICONS (Custom SVG for zero dependencies) ===
const CheckCircle = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const Lock = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const BookOpen = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const ArrowDown = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <polyline points="19 12 12 19 5 12"></polyline>
  </svg>
);

const ArrowRight = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const ArrowLeft = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const XIcon = ({ size = 24, className = "", style = {} }: { size?: number, className?: string, style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

interface TermsAndPrivacyConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const TermsAndPrivacyConsent: React.FC<TermsAndPrivacyConsentProps> = ({ checked, onChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <AgreementRow
        isAgreed={checked}
        onClick={() => { setIsModalOpen(prev => !prev); }}
      />

      {isModalOpen && createPortal(
        <AgreementModal
          isAlreadyAgreed={checked}
          onClose={() => setIsModalOpen(false)}
          onAgree={() => {
            onChange(true);
            setIsModalOpen(false);
          }}
        />,
        document.body
      )}
    </div>
  );
};

export default TermsAndPrivacyConsent;

function AgreementRow({ isAgreed, onClick }: { isAgreed: boolean, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      style={{
        width: '100%',
        textAlign: 'left',
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        marginBottom: '20px'
      }}
      onMouseEnter={(e) => {
        if (!isAgreed) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.03)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: '5px',
        border: '1.5px solid',
        borderColor: isAgreed ? '#0d6efd' : '#d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        backgroundColor: isAgreed ? '#0d6efd' : '#ffffff'
      }}>
        {isAgreed && <CheckCircle size={14} className="text-white" />}
      </div>

      <div style={{ flex: 1, fontSize: '13.5px', lineHeight: '1.6', color: isAgreed ? '#0f172a' : '#475569' }}>
        I have read and agree to the{' '}
        <a
          href="https://constacloud.com/commercium/terms-of-use.html"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: '#0d6efd',
            fontWeight: '600',
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#0a58ca'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#0d6efd'}
        >
          Terms & Conditions
        </a>
        {' '}and{' '}
        <a
          href="https://constacloud.com/commercium/privacy-policy.html"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: '#0d6efd',
            fontWeight: '600',
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#0a58ca'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#0d6efd'}
        >
          Privacy Policy
        </a>
      </div>


    </button>
  );
}

type DocumentSection = 'terms' | 'privacy';

const DOCUMENT_HEADERS: Record<DocumentSection, { title: string; subtitles: string[] }> = {
  terms: {
    title: 'Terms of Service',
    subtitles: ['Effective Date: May 11, 2026 | Last Updated: May 11, 2026'],
  },
  privacy: {
    title: 'Privacy Policy',
    subtitles: [
      'Commercium by ConstaCloud Private Limited',
      'GDPR Compliant · Data Secure',
      'Last Updated: 8th May, 2026',
    ],
  },
};

function DocumentHeaderBlock({ section }: { section: DocumentSection }) {
  const { title, subtitles } = DOCUMENT_HEADERS[section];

  return (
    <div>
      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
        {title}
      </h3>
      {subtitles.map((line, index) => (
        <p
          key={line}
          style={{
            margin: index === 0 ? '2px 0 0 0' : '1px 0 0 0',
            fontSize: '12px',
            color: '#64748b',
            lineHeight: '1.4',
          }}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

export function AgreementModal({ onClose, onAgree, isAlreadyAgreed, isGoogleFlow }: { onClose: () => void, onAgree: () => void, isAlreadyAgreed: boolean, isGoogleFlow?: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const privacySectionRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<DocumentSection>('terms');

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const privacyEl = privacySectionRef.current;
    if (!scrollEl || !privacyEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setActiveSection(entry.isIntersecting ? 'privacy' : 'terms');
      },
      {
        root: scrollEl,
        threshold: 0,
        rootMargin: '-72px 0px 0px 0px',
      }
    );

    observer.observe(privacyEl);
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 2147483647,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .consent-modal-scrollbar::-webkit-scrollbar { width: 6px; }
        .consent-modal-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .consent-modal-scrollbar::-webkit-scrollbar-thumb { background: rgba(13, 110, 253, 0.15); border-radius: 10px; }
        .consent-modal-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(13, 110, 253, 0.3); }
        @keyframes consentFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes consentModalSlide { from { opacity: 0; transform: scale(0.95) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes bounceLocal { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}} />

      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: 'consentFadeIn 0.3s ease-out forwards'
        }}
      />

      <div style={{
        position: 'relative',
        background: '#ffffff',
        width: '100%',
        maxWidth: '680px',
        maxHeight: 'min(820px, 90vh)',
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px -10px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        animation: 'consentModalSlide 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards'
      }}>
        <div style={{ position: 'relative', zIndex: 10, background: '#ffffff', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ padding: '24px 32px 20px 32px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', minWidth: 0 }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(13, 110, 253, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BookOpen size={20} style={{ color: '#0d6efd' }} />
              </div>
              <DocumentHeaderBlock section={activeSection} />
            </div>
            <button
              onClick={onClose}
              style={{
                border: 'none',
                background: '#f8fafc',
                padding: '10px',
                borderRadius: '10px',
                cursor: 'pointer',
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                outline: 'none',
                flexShrink: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; }}
            >
              <XIcon size={18} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#ffffff', display: 'flex', flexDirection: 'column' }}>
          <div
            ref={scrollRef}
            className="consent-modal-scrollbar"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '40px 48px',
            }}
          >
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <TermsContent />
              <div style={{ height: '40px' }} />
              <div ref={privacySectionRef}>
                <PrivacyContent />
              </div>
            </div>
          </div>

        </div>

        <div style={{ padding: '24px 32px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
          <button
            onClick={onAgree}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '14px',
              fontSize: '15px',
              fontWeight: '700',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: '#0d6efd',
              color: '#ffffff',
              boxShadow: '0 10px 20px -5px rgba(13, 110, 253, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              outline: 'none'
            }}
          >
            {isAlreadyAgreed ? 'Already Accepted' : (
              isGoogleFlow ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Accept & Continue with Google
                  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px', background: 'white', borderRadius: '50%', padding: '2px' }}>
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                </div>
              ) : (
                <>Accept & Continue <CheckCircle size={18} /></>
              )
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabItem({ active, completed, children, onClick }: { active: boolean, completed: boolean, children: string, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0 0 16px 0',
        fontSize: '14px',
        fontWeight: '700',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        position: 'relative',
        color: active ? '#0d6efd' : '#64748b',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease',
        outline: 'none'
      }}
    >
      {children}
      {completed && <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#10b981', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>✓</div>}
      {active && <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: '2.5px', background: '#0d6efd', borderRadius: '3px 3px 0 0' }} />}
    </button>
  );
}

function LegalSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: '28px' }}>
      <h5 style={{
        fontSize: '15.5px',
        fontWeight: '700',
        color: '#0f172a',
        marginBottom: '10px',
        letterSpacing: '-0.01em'
      }}>
        {title}
      </h5>
      <p style={{
        fontSize: '14.5px',
        lineHeight: '1.75',
        color: '#475569',
        margin: 0
      }}>
        {children}
      </p>
    </section>
  );
}

function TermsContent() {
  return (
    <div style={{ color: "#475569" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <LegalSection title="01. Simple Summary">
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            <li>
              Commercium helps businesses connect and automate systems —
              marketplaces, ERPs, shipping, fulfillment, and e-commerce channels.
            </li>
            <li>
              You own your data. We process it to provide the service.
            </li>
            <li>
              The platform depends on third-party systems and APIs.
            </li>
            <li>
              We aim to provide reliable service, but cannot guarantee
              uninterrupted or error-free operation.
            </li>
            <li>
              Your use of Commercium is subject to these Terms, applicable fees,
              and any order form or proposal agreed with you.
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="02. Acceptance of Terms">
          <p>
            These Terms of Service ("Terms") govern your access to and use of
            Commercium, a software product owned and operated by ConstaCloud
            Private Limited, together with its affiliates including
            ConstaCloud LLC where applicable.
          </p>

          <div style={{ marginTop: "12px" }}>
            <strong>
              In these Terms, "ConstaCloud", "we", "us", or "our" refers to:
            </strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>🇮🇳 ConstaCloud Private Limited — India</li>
            <li>🇺🇸 ConstaCloud LLC — United States</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            Unless a separate order form, invoice, proposal, or written
            agreement states otherwise, the primary contracting entity for
            Commercium shall be ConstaCloud Private Limited.
          </p>

          <p style={{ marginTop: "12px" }}>
            By accessing, using, subscribing to, or paying for Commercium, you
            agree to be bound by these Terms. If you do not agree, you must not
            use the service.
          </p>
        </LegalSection>

        <LegalSection title="03. Billing Entity and Payment Collection">
          <p>
            Depending on your location, payment method, tax requirements, or
            commercial arrangement, invoices may be issued by either:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>🇮🇳 ConstaCloud Private Limited</li>
            <li>🇺🇸 ConstaCloud LLC</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            Payment to either entity shall be treated as payment for the
            Commercium services.
          </p>

          <p style={{ marginTop: "12px" }}>
            Use of ConstaCloud LLC for invoicing or payment collection does not
            change the governing law, dispute resolution process, or
            jurisdiction stated in these Terms unless expressly agreed in a
            separate written agreement signed by ConstaCloud.
          </p>
        </LegalSection>

        <LegalSection title="04. Service Description">
          <p>
            Commercium is a Software-as-a-Service platform that provides
            e-commerce and business system integration services, including:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Product synchronization</li>
            <li>Inventory and pricing updates</li>
            <li>Order management</li>
            <li>Shipping and fulfillment automation</li>
            <li>Marketplace integrations</li>
            <li>
              ERP, accounting, POS, warehouse, and logistics integrations
            </li>
            <li>Workflow automation and related services</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            We may modify, enhance, suspend, or discontinue features,
            integrations, workflows, or platform functionality from time to
            time.
          </p>
        </LegalSection>

        <LegalSection title="05. Third-Party Dependencies">
          <p>
            Commercium integrates with third-party platforms including
            marketplaces, ERPs, accounting platforms, shipping providers,
            fulfillment providers, POS systems, APIs, and other external
            systems.
          </p>

          <div style={{ marginTop: "12px" }}>
            <strong>You acknowledge that:</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>These systems are outside our control</li>
            <li>
              APIs, rules, data structures, permissions, pricing, and
              availability may change without notice
            </li>
            <li>
              Delays, errors, interruptions, or data inconsistencies may occur
              due to third-party systems
            </li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            ConstaCloud shall not be liable for failures, delays, or losses
            caused by third-party platforms, APIs, or external service
            providers.
          </p>
        </LegalSection>

        <LegalSection title="06. User Responsibilities">
          <p>You agree to:</p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Provide accurate and complete information</li>
            <li>Maintain correct system configurations</li>
            <li>
              Safeguard login details, API keys, tokens, and credentials
            </li>
            <li>
              Ensure you have authority to connect third-party accounts
            </li>
            <li>
              Comply with applicable third-party platform terms and policies
            </li>
            <li>
              Monitor business-critical outputs such as orders, inventory,
              pricing, stock levels, tracking, and financial data
            </li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            You are responsible for validating business-critical data before
            acting on it.
          </p>
        </LegalSection>

        <LegalSection title="07. Data Accuracy and Synchronization Disclaimer">
          <p>
            While we strive to provide reliable synchronization, Commercium does
            not guarantee real-time, uninterrupted, or error-free data transfer.
            Data inconsistencies may occur due to:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>
              API delays, rate limits, network issues, or credential expiry
            </li>
            <li>
              Third-party platform issues or marketplace/ERP-side errors
            </li>
            <li>
              Platform policy changes or manual changes in connected systems
            </li>
            <li>Incorrect customer configuration</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            You remain responsible for reviewing and verifying important
            operational, financial, inventory, and order data.
          </p>
        </LegalSection>

        <LegalSection title="08. Payments, Billing, and Suspension">
          <p>
            Services are provided on a subscription, usage-based, project-based,
            or agreed commercial basis. You agree to pay all applicable fees as
            stated in the applicable invoice, order form, proposal, or
            subscription plan.
          </p>

          <div style={{ marginTop: "12px" }}>
            <strong>If payment is overdue, we may:</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Suspend access to Commercium</li>
            <li>Pause integrations or data sync</li>
            <li>Stop support or implementation work</li>
            <li>Terminate the service</li>
            <li>
              Withhold further work until outstanding amounts are cleared
            </li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            We are not obligated to continue providing services during payment
            disputes, overdue periods, chargebacks, or failed payments.
          </p>
        </LegalSection>

        <LegalSection title="09. Termination">
          <p>We may suspend or terminate your access if:</p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>You violate these Terms</li>
            <li>You fail to pay fees when due</li>
            <li>You misuse the service</li>
            <li>There is a security, legal, or reputational risk</li>
            <li>You violate third-party platform terms</li>
            <li>
              The service becomes commercially or technically impractical
            </li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            Upon termination: access may be revoked immediately, data is
            retained up to 30 days then deleted, and outstanding fees remain
            payable.
          </p>
        </LegalSection>

        <LegalSection title="10. Intellectual Property">
          <p>
            All software, systems, workflows, code, interfaces, documentation,
            designs, know-how, processes, templates, connectors, and technology
            related to Commercium are owned by or licensed to ConstaCloud.
          </p>

          <p style={{ marginTop: "12px" }}>
            You receive a limited, non-exclusive, non-transferable, revocable
            license to use Commercium during your active subscription or service
            term.
          </p>

          <p style={{ marginTop: "12px" }}>
            You may not copy, reverse engineer, resell, sublicense, reproduce,
            modify, or create derivative works from Commercium unless expressly
            permitted in writing.
          </p>
        </LegalSection>

        <LegalSection title="11. Integration Scope and Customization">
          <p>
            Standard subscriptions include predefined features, workflows, and
            integrations. The following may require additional fees:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>
              Custom workflows, new integrations, ERP-specific modifications
            </li>
            <li>
              Marketplace-specific changes or custom reports
            </li>
            <li>
              Historical data migration, backfilling, and reconciliation work
            </li>
            <li>Changes due to third-party API updates</li>
            <li>Additional marketplace or ERP instances</li>
            <li>Support beyond agreed scope</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            Unless expressly agreed in writing, such work is not included in
            standard subscription fees.
          </p>
        </LegalSection>

        <LegalSection title="12. Warranties and Disclaimer">
          <p>
            Commercium is provided on an "as is" and "as available" basis. To
            the fullest extent permitted by law, we disclaim all warranties,
            whether express, implied, statutory, or otherwise.
          </p>

          <div style={{ marginTop: "12px" }}>
            <strong>We do not guarantee:</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Uninterrupted service or error-free operation</li>
            <li>Compatibility with all systems</li>
            <li>Continuous availability of third-party APIs</li>
            <li>Specific business results</li>
            <li>
              Prevention of all data loss, overselling, pricing errors, order
              delays, or operational issues
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="13. Limitation of Liability">
          <p>
            To the fullest extent permitted by law, ConstaCloud's total
            aggregate liability shall not exceed the fees paid by you during the
            three months immediately preceding the claim.
          </p>

          <p style={{ marginTop: "12px" }}>
            ConstaCloud shall not be liable for:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Loss of profits, revenue, business, or goodwill</li>
            <li>Loss of data or customers</li>
            <li>Marketplace suspension or third-party penalties</li>
            <li>Operational downtime or consequential damages</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            This limitation applies whether the claim is based on contract,
            tort, negligence, strict liability, statute, or any other legal
            theory.
          </p>
        </LegalSection>

        <LegalSection title="14. Force Majeure">
          <p>
            ConstaCloud shall not be liable for any delay, interruption, or
            failure caused by events beyond its reasonable control, including
            natural disasters, acts of government, war or civil unrest,
            internet or telecom failures, cloud infrastructure outages,
            third-party API failures, cyberattacks, power failures, pandemics,
            or labor disputes.
          </p>

          <p style={{ marginTop: "12px" }}>
            During such events, affected obligations shall be suspended for the
            duration of the event and a reasonable recovery period thereafter.
          </p>
        </LegalSection>

        <LegalSection title="15. Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless ConstaCloud
            (including ConstaCloud Private Limited, ConstaCloud LLC, directors,
            officers, employees, contractors, and affiliates) from any claims,
            damages, losses, liabilities, penalties, costs, and expenses
            arising from:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Your misuse of Commercium</li>
            <li>Incorrect configurations</li>
            <li>Unauthorized use of third-party accounts</li>
            <li>Violation of third-party platform terms</li>
            <li>Violation of applicable laws</li>
            <li>Data, content, or instructions provided by you</li>
            <li>
              Business decisions made based on synced or processed data
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="16. Data Protection and Privacy">
          <p>
            Your use of Commercium is also governed by our Privacy Policy and,
            where applicable, our Data Processing Addendum.
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>You retain ownership of your data</li>
            <li>
              We process customer data to provide, maintain, secure, improve,
              and support Commercium
            </li>
            <li>
              We may use subprocessors, hosting providers, monitoring tools,
              support tools, analytics tools, and other service providers to
              deliver the service
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="17. Modifications to Terms">
          <p>
            We may update these Terms from time to time. Updated Terms will be
            published on our website or otherwise notified to you.
          </p>

          <p style={{ marginTop: "12px" }}>
            Continued use of Commercium after updated Terms become effective
            constitutes acceptance of the updated Terms.
          </p>

          <p style={{ marginTop: "12px" }}>
            For material changes, we may require explicit acceptance before
            continued use, renewal, configuration changes, support requests, or
            other service activity.
          </p>
        </LegalSection>

        <LegalSection title="18. Governing Law, Jurisdiction, and Dispute Resolution">
          <p>
            These Terms shall be governed by and interpreted in accordance with
            the laws of India, without regard to conflict-of-law principles.
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Disputes are first resolved through good-faith negotiation</li>
            <li>
              Then through arbitration under the Arbitration and Conciliation
              Act, 1996
            </li>
            <li>
              Seat of arbitration: Korba, Chhattisgarh, India — Language:
              English
            </li>
            <li>
              Exclusive jurisdiction: Courts in Korba, Chhattisgarh, India
            </li>
            <li>
              Invoicing by ConstaCloud LLC does not alter governing law or
              jurisdiction
            </li>
          </ul>
        </LegalSection>
      </div>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div style={{ color: "#475569" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <LegalSection title="1. Introduction">
          <p>
            This Privacy Policy describes how ConstaCloud Private Limited
            (India) and ConstaCloud LLC (USA) (collectively, “ConstaCloud”,
            “we”, “our”, or “us”) collect, use, disclose, and protect personal
            data in connection with the Commercium platform.
          </p>

          <p style={{ marginTop: "12px" }}>
            Commercium is designed as a secure data infrastructure layer,
            enabling businesses to connect and synchronize data across
            marketplaces, ERP systems, e-commerce platforms, and other
            third-party systems.
          </p>
        </LegalSection>

        <LegalSection title="2. Data Protection Roles">
          <p>Depending on the context:</p>

          <div style={{ marginTop: "16px" }}>
            <strong>Data Controller</strong>
            <div style={{ marginTop: "6px" }}>
              For account registration, billing, website usage, and support
              interactions.
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <strong>Data Processor</strong>
            <div style={{ marginTop: "6px" }}>
              For all customer data processed through Commercium on behalf of
              our clients.
            </div>
          </div>

          <p style={{ marginTop: "12px" }}>
            We process customer data strictly on documented instructions.
          </p>
        </LegalSection>

        <LegalSection title="3. Categories of Data We Process">
          <div>
            <strong>A. Account & Business Data</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Name, email, company details</li>
            <li>Billing and subscription information</li>
          </ul>

          <p style={{ marginTop: "10px" }}>
            <strong>Purpose:</strong> Account management
          </p>

          <p style={{ marginTop: "6px" }}>
            <strong>Legal Basis:</strong> Contract performance - (Article
            6(1)(b))
          </p>

          <div style={{ marginTop: "20px" }}>
            <strong>B. Customer Data (Processed on behalf of clients)</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Customer names, email addresses, phone numbers</li>
            <li>Shipping and billing addresses</li>
            <li>Order details, transaction records</li>
            <li>
              Customer messages (if integrated via support systems)
            </li>
            <li>Payout and settlement data</li>
          </ul>

          <p style={{ marginTop: "10px" }}>
            <strong>Purpose:</strong> Data synchronization across connected
            systems
          </p>

          <p style={{ marginTop: "6px" }}>
            <strong>Legal Basis:</strong> Contract performance - (Article
            6(1)(b))
          </p>

          <div style={{ marginTop: "20px" }}>
            <strong>C. Technical & Usage Data</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>IP address, device information</li>
            <li>Logs and system activity</li>
            <li>API usage data</li>
            <li>
              Security, monitoring, and service improvement
            </li>
          </ul>

          <p style={{ marginTop: "10px" }}>
            <strong>Legal Basis:</strong> Legitimate interests - (Article
            6(1)(f))
          </p>

          <div style={{ marginTop: "20px" }}>
            <strong>D. Website Analytics Data</strong>
          </div>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>
              Collected via tools like Google Analytics and Mouseflow
            </li>
          </ul>

          <p style={{ marginTop: "10px" }}>
            <strong>Purpose:</strong> Website performance and user behavior
            insights
          </p>

          <p style={{ marginTop: "6px" }}>
            <strong>Legal Basis:</strong> Consent - (Article 6(1)(a))
          </p>
        </LegalSection>

        <LegalSection title="4. How We Use Data">
          <p>We use personal data to:</p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Provide and operate the Commercium platform</li>
            <li>Synchronize data between integrated systems</li>
            <li>Monitor performance and ensure reliability</li>
            <li>Provide customer support</li>
            <li>Ensure platform security and fraud prevention</li>
          </ul>
        </LegalSection>

        <LegalSection title="5. Sub-Processors">
          <p>
            We use trusted third-party providers to support our services.
          </p>

          <div style={{ marginTop: "16px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #e2e8f0",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid #e2e8f0",
                      padding: "10px",
                      textAlign: "left",
                      background: "#f8fafc",
                    }}
                  >
                    Provider
                  </th>

                  <th
                    style={{
                      border: "1px solid #e2e8f0",
                      padding: "10px",
                      textAlign: "left",
                      background: "#f8fafc",
                    }}
                  >
                    Purpose
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    AWS
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Hosting and infrastructure
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Cloudflare
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    CDN, security, and DDoS protection
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Google Workspace
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Email communication
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Google Analytics
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Website analytics
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Mouseflow
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    User behavior analytics
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Rollbar
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Error monitoring
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Pulsetic
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Uptime monitoring
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Tawk.to
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Customer support
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    SE Rankings
                  </td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    SEO monitoring (limited use)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: "12px" }}>
            All sub-processors are contractually bound to protect data and
            comply with applicable data protection laws.
          </p>
        </LegalSection>

        <LegalSection title="6. International Data Transfers">
          <p>
            Personal data may be processed outside the European Economic Area
            (EEA), including in the United States and India. Where such
            transfers occur, we rely on:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Standard Contractual Clauses (SCCs)</li>
            <li>Other legally recognized safeguards</li>
          </ul>
        </LegalSection>

        <LegalSection title="7. Data Retention">
          <p>We retain data as follows:</p>

          <div style={{ marginTop: "16px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #e2e8f0",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid #e2e8f0",
                      padding: "10px",
                      textAlign: "left",
                      background: "#f8fafc",
                    }}
                  >
                    Data Type
                  </th>

                  <th
                    style={{
                      border: "1px solid #e2e8f0",
                      padding: "10px",
                      textAlign: "left",
                      background: "#f8fafc",
                    }}
                  >
                    Retention Period
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Account data
                  </td>

                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    60 days after termination
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    System logs
                  </td>

                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    30 days
                  </td>
                </tr>

                <tr>
                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    Backup data
                  </td>

                  <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                    60 days
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: "12px" }}>
            After these periods, data is securely deleted or anonymized.
          </p>
        </LegalSection>

        <LegalSection title="8. Security Measures">
          <p>
            We implement industry-standard security practices, including:
          </p>

          <ul style={{ marginTop: "12px", paddingLeft: "18px" }}>
            <li>🔐 Encryption at rest (AES-256)</li>
            <li>🔒 Encryption in transit (TLS 1.2+)</li>
            <li>👤 Role-based access control (RBAC)</li>
            <li>📋 Audit logging & monitoring</li>
            <li>☁️ Secure cloud infrastructure (AWS)</li>
          </ul>
        </LegalSection>

        <LegalSection title="9. Data Subject Rights">
          <p>Under GDPR, individuals have the right to:</p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Access their data</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion</li>
            <li>Restrict processing</li>
            <li>Data portability</li>
            <li>Object to processing</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            Requests can be submitted to{" "}
            <a
              href="mailto:care@constacloud.com"
              style={{
                color: "#0d6efd",
                textDecoration: "underline",
                fontWeight: "500",
              }}
            >
              care@constacloud.com
            </a>
            . We respond within 30 days.
          </p>
        </LegalSection>

        <LegalSection title="10. Breach Notification">
          <p>
            In the event of a personal data breach, we will notify affected
            customers without undue delay, in accordance with applicable laws.
          </p>
        </LegalSection>

        <LegalSection title="11. Automated Decision-Making">
          <p>
            We do not engage in automated decision-making or profiling that
            produces legal or significant effects.
          </p>
        </LegalSection>

        <LegalSection title="12. Cookies & Tracking">
          <p>
            We use cookies and similar technologies for:
          </p>

          <ul style={{ marginTop: "10px", paddingLeft: "18px" }}>
            <li>Analytics (Google Analytics)</li>
            <li>User behavior tracking (Mouseflow)</li>
          </ul>

          <p style={{ marginTop: "12px" }}>
            Users may manage cookie preferences via browser settings or consent
            mechanisms.
          </p>
        </LegalSection>

        <LegalSection title="13. Data Processing Agreement (DPA)">
          <p>
            A Data Processing Addendum (DPA) is available upon request and
            governs our obligations as a data processor.
          </p>
        </LegalSection>

        <LegalSection title="14. Updates to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Material
            changes will be communicated via email or platform notification.
          </p>
        </LegalSection>

        <LegalSection title="15. Contact Information">
          <div>ConstaCloud Private Limited — India</div>

          <div style={{ marginTop: "6px" }}>
            ConstaCloud LLC — Delaware, USA
          </div>

          <div style={{ marginTop: "12px" }}>
            ✉️{" "}
            <a
              href="mailto:care@constacloud.com"
              style={{
                color: "#0d6efd",
                textDecoration: "underline",
                fontWeight: "500",
              }}
            >
              care@constacloud.com
            </a>
          </div>
        </LegalSection>
      </div>
    </div>
  );
}



